import type {Copilot} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {type BoardCardAddress, useBoardFocus} from '@github-ui/board-core'
import {registerSettlementHandler} from '@github-ui/optimistic-connection/settlementRegistry'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {AgentIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {
  createContext,
  type ReactNode,
  type RefObject,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import type {BoardPayloadAssignee, BoardPayloadIssueItem, BoardPayloadItem} from './board-payload'
import {type BoardCardActionsSource, boardCardActionsQueryOptions} from './card-actions'

interface AgentAssignmentTarget {
  item: BoardPayloadIssueItem
  /** The menu button the dialog was opened from. */
  opener: HTMLElement | null
  /** The card the dialog was opened from, if it was on the page. */
  cardElement: Element | null
  /** Puts focus back on the card, for when the menu that opened the dialog is gone. */
  returnFocus: () => void
}

/** Whether any assignee is an agent, Copilot or otherwise. */
export function hasAgentAssignee(assignees: readonly BoardPayloadAssignee[]): boolean {
  return assignees.some(assignee => assignee.isAgent)
}

/**
 * The agent each issue was assigned to on this board, by global id. The board is read from an
 * index that has not seen the assignment yet, so its cards draw the agent from here until it has.
 * A store rather than state, so an assignment redraws the avatar only on that issue's cards.
 *
 * An entry is dropped once every loaded copy of its issue lists an agent, since one issue can sit
 * in several cells and a cell paged in later can be fresher than the rest. From then on the payload
 * is the truth, so a later unassignment shows on refresh.
 */
export class AssignedAgents {
  // Replaced rather than changed, so a reader of the whole map sees when it changes.
  #agents: ReadonlyMap<string, BoardPayloadAssignee> = new Map()
  #listedByBoard = new Set<string>()
  #listeners = new Set<() => void>()

  get = (globalId: string) => this.#agents.get(globalId)

  getSnapshot = () => this.#agents

  set(globalId: string, agent: BoardPayloadAssignee) {
    // A response that lands after the board already lists the agent has nothing left to stand in for.
    if (this.#listedByBoard.has(globalId)) return
    this.#agents = new Map(this.#agents).set(globalId, agent)
    this.#notify()
  }

  /** Drops an assignment a later confirmed assignee edit has replaced, so the card follows that edit. */
  forget(globalId: string) {
    if (!this.#agents.has(globalId)) return
    const remaining = new Map(this.#agents)
    remaining.delete(globalId)
    this.#agents = remaining
    this.#notify()
  }

  /** Reads every item the board holds, offscreen cells included. */
  reconcile(items: readonly BoardPayloadItem[]) {
    const listing = new Map<string, boolean>()
    for (const item of items) {
      if (item.type !== 'issue') continue
      const lists = hasAgentAssignee(item.fields.assignee?.value ?? [])
      listing.set(item.globalId, (listing.get(item.globalId) ?? true) && lists)
    }

    this.#listedByBoard = new Set([...listing].filter(([, lists]) => lists).map(([globalId]) => globalId))
    const remaining = new Map([...this.#agents].filter(([globalId]) => !this.#listedByBoard.has(globalId)))
    if (remaining.size === this.#agents.size) return
    this.#agents = remaining
    this.#notify()
  }

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  #notify() {
    for (const listener of this.#listeners) listener()
  }
}

type OpenAgentAssignment = (target: AgentAssignmentTarget) => void

const OpenAgentAssignmentContext = createContext<OpenAgentAssignment | null>(null)
const AssignedAgentsContext = createContext<AssignedAgents | null>(null)

/** Without a provider, cards offer no assignment. */
function useOpenAgentAssignment(): OpenAgentAssignment | null {
  return use(OpenAgentAssignmentContext)
}

const subscribeToNothing = () => () => {}
const nothing = () => undefined

/**
 * The agent this board assigned the issue to, while this copy of the payload lists none of its own.
 * A copy that lists one draws that instead, so the agent is never drawn twice.
 */
export function useAssignedAgent(globalId: string, payloadListsAgent: boolean): BoardPayloadAssignee | undefined {
  const store = use(AssignedAgentsContext)
  const agent = useSyncExternalStore(store?.subscribe ?? subscribeToNothing, () => store?.get(globalId), nothing)

  return payloadListsAgent ? undefined : agent
}

/** The repository the assignment endpoint is addressed through, or null for a name it cannot address. */
export function assignmentRepository(item: BoardPayloadIssueItem): {name: string; owner: {login: string}} | null {
  const [owner, name, ...rest] = item.repoNameWithOwner.split('/')
  if (!owner || !name || rest.length > 0) return null
  return {name, owner: {login: owner}}
}

/**
 * Whether a card may offer "Assign to agent", before asking the server: the board may offer it, the
 * issue is open, has no agent listed or assigned from this board, and names a repository the
 * endpoint can address. The server then answers whether this viewer may assign this issue.
 */
function canOfferAgentAssignment(
  item: BoardPayloadItem,
  assignedAgents: ReadonlyMap<string, BoardPayloadAssignee>,
): item is BoardPayloadIssueItem {
  return (
    item.type === 'issue' &&
    item.state === 'open' &&
    !hasAgentAssignee(item.fields.assignee?.value ?? []) &&
    assignedAgents.get(item.globalId) === undefined &&
    assignmentRepository(item) !== null
  )
}

/**
 * The board's `onCardMenuIntent`: asks whether a card may be assigned as the reader points at or
 * focuses its menu button, so the menu usually opens with the answer.
 */
export function useAgentAssignmentMenuIntent(
  assignedAgents: AssignedAgents,
  source: BoardCardActionsSource | undefined,
) {
  const queryClient = useQueryClient()

  return useMemo(
    () =>
      source
        ? (item: BoardPayloadItem) => {
            if (!canOfferAgentAssignment(item, assignedAgents.getSnapshot())) return
            void queryClient.prefetchQuery(boardCardActionsQueryOptions(item, source, queryClient))
          }
        : undefined,
    [assignedAgents, queryClient, source],
  )
}

/**
 * The card menu's "Assign to agent" item, which opens the board's assignment dialog. Drawn loading
 * until the server answers. A refusal or failure known as the menu opens draws nothing; one that
 * arrives while the item is drawn leaves it inactive, so focus on it is not lost. Reopening the menu
 * asks again after a failure.
 *
 * A group of its own after the card's moves: it changes the issue itself, not just its place on the board.
 */
function AssignToAgentAction({
  item,
  card,
  source,
}: {
  item: BoardPayloadIssueItem
  card: BoardCardAddress
  source: BoardCardActionsSource
}) {
  const open = useOpenAgentAssignment()
  const {focusPosition} = useBoardFocus()
  const queryClient = useQueryClient()
  const {data, isPending, isFetching, isError} = useQuery(boardCardActionsQueryOptions(item, source, queryClient))
  // A retry after a failure stays in the error status while it asks.
  const isAsking = isPending || (data === undefined && isFetching)
  const [wasDrawnAsking] = useState(isAsking)
  if (!open) return null

  const isAvailable = data?.assignAgent?.available === true
  if (!isAsking && !isAvailable && !wasDrawnAsking) return null
  const unavailableReason = isError ? 'Could not check for this issue' : 'Not available for this issue'

  const onSelect = () => {
    const returnFocus = () => focusPosition({...card, kind: 'cell'})
    // Open after the menu has refocused its button, so the dialog returns focus there.
    requestAnimationFrame(() => {
      const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
      // The board's own card attribute; it documents no other way to find the card element.
      const cardElement = opener?.closest('[data-board-card-id]') ?? null
      open({item, opener, cardElement, returnFocus})
    })
  }

  return (
    <>
      <ActionList.Divider />
      <ActionList.Item
        // Disabled while asking too, since loading alone leaves the item announced as one that can be chosen.
        loading={isAsking}
        disabled={isAsking}
        inactiveText={isAsking || isAvailable ? undefined : unavailableReason}
        onSelect={isAsking || !isAvailable ? undefined : onSelect}
      >
        <ActionList.LeadingVisual>
          <AgentIcon />
        </ActionList.LeadingVisual>
        Assign to agent
      </ActionList.Item>
    </>
  )
}

/**
 * The board's `renderCardTrailingActions`. Rebuilt, redrawing every card's menu, whenever an assignment
 * changes which cards may offer one: the board draws a menu for any element, so only `null` hides one.
 * Offers nothing without a source to ask.
 */
export function useAgentAssignmentCardActions(
  assignedAgents: AssignedAgents,
  source: BoardCardActionsSource | undefined,
) {
  const agents = useSyncExternalStore(assignedAgents.subscribe, assignedAgents.getSnapshot, assignedAgents.getSnapshot)

  return useCallback(
    (item: BoardPayloadItem, card: BoardCardAddress) =>
      source && canOfferAgentAssignment(item, agents) ? (
        <AssignToAgentAction item={item} card={card} source={source} />
      ) : null,
    [agents, source],
  )
}

/**
 * After the dialog or the board has redrawn, puts focus on the card if it was left nowhere: on the
 * page, or on the board around the card, which is where it falls when the card's menu goes away.
 */
function returnFocusIfLost(target: AgentAssignmentTarget) {
  requestAnimationFrame(() => {
    const active = document.activeElement
    const {cardElement} = target
    const isAroundCard = cardElement !== null && active !== cardElement && !!active?.contains(cardElement)
    if (active === null || active === document.body || isAroundCard) target.returnFocus()
  })
}

/**
 * Hosts the one assignment dialog every card on the board opens. The host reconciles
 * `assignedAgents` with the items the board holds.
 */
export function BoardAgentAssignmentProvider({
  assignedAgents,
  children,
}: {
  assignedAgents: AssignedAgents
  children: ReactNode
}) {
  const [target, setTarget] = useState<AgentAssignmentTarget | null>(null)
  const openTargetRef = useRef(target)
  const closedTargetRef = useRef<AgentAssignmentTarget | null>(null)
  useEffect(() => {
    openTargetRef.current = target
    const closed = closedTargetRef.current
    closedTargetRef.current = null
    // Runs once the dialog has unmounted and tried to return focus itself.
    if (target === null && closed) returnFocusIfLost(closed)
  }, [target])

  useEffect(
    () =>
      registerSettlementHandler(async operation => {
        if (operation.type === 'setFields' && 'assignedActors' in operation.fields) assignedAgents.forget(operation.id)
      }),
    [assignedAgents],
  )

  // Scoped to its own target: a request that lands after its dialog was dismissed must not close a newer one.
  const close = useCallback(() => {
    if (target !== null && openTargetRef.current === target) closedTargetRef.current = target
    setTarget(current => (current === target ? null : current))
  }, [target])

  const onAssignmentComplete = useCallback(
    (agent?: Copilot) => {
      if (!target || !agent) return
      // Only when focus is still on the menu this assignment removes; the reader may have moved on.
      const heldFocus = target.opener !== null && document.activeElement === target.opener
      assignedAgents.set(target.item.globalId, toBoardAssignee(agent))
      if (heldFocus) returnFocusIfLost(target)
    },
    [assignedAgents, target],
  )

  const repository = useMemo(() => (target ? assignmentRepository(target.item) : null), [target])

  // A button the assignment removed takes no focus, which leaves the fallback above to place it.
  const returnFocusRef = useMemo<RefObject<HTMLElement | null>>(() => ({current: target?.opener ?? null}), [target])

  return (
    <OpenAgentAssignmentContext value={setTarget}>
      <AssignedAgentsContext value={assignedAgents}>
        {children}
        {target && repository && (
          <CopilotAssignmentModal
            isOpen
            onClose={close}
            repository={repository}
            issueNumbers={[target.item.number]}
            onAssignmentComplete={onAssignmentComplete}
            returnFocusRef={returnFocusRef}
          />
        )}
      </AssignedAgentsContext>
    </OpenAgentAssignmentContext>
  )
}

function toBoardAssignee(agent: Copilot): BoardPayloadAssignee {
  return {
    login: agent.login,
    name: agent.name ?? null,
    avatarUrl: agent.avatarUrl,
    isAgent: true,
    isCopilot: agent.isCopilot ?? false,
  }
}
